'use client';

import { useState, useEffect, useRef, useCallback } from 'react';

/** Total wall-clock time before loader timeout and error UI (seconds). */
export const CALCULATOR_LOADER_TOTAL_SECONDS = 90;

/**
 * After this many wall-clock seconds, the on-screen timer “resets” to 30s and the
 * long-wait text is shown (see `calculator.stepper_info.loader_taking_longer`).
 */
export const CALCULATOR_LOADER_LONG_WAIT_ELAPSED_SECONDS = 60;

/**
 * @param {number} remainingTotal - Countdown of total time left (e.g. 90 → 0)
 * @param {number} total - Initial total (default 90)
 * @returns {number} Seconds to show in "M:SS" in the first phase: 1:00…0:01, then second phase: 0:30…0:00
 */
export function getLoaderDisplayTimerSeconds(remainingTotal, total) {
  if (total <= 0) {
    return 0;
  }
  const lastPhaseSeconds = (total * 30) / 90; // 30s of 90, scaled if total changes
  if (remainingTotal > lastPhaseSeconds) {
    return Math.ceil(remainingTotal - lastPhaseSeconds);
  }
  return remainingTotal;
}

/**
 * @param {object} options
 * @param {boolean} options.active - When true, runs countdown from `totalSeconds` to 0.
 * @param {() => void} [options.onTimeout] - Fired once when countdown reaches 0 (once per `active` session).
 * @param {number} [options.totalSeconds] - Default 90.
 */
export function useCalculatorLoaderTimer({
  active,
  onTimeout,
  totalSeconds = CALCULATOR_LOADER_TOTAL_SECONDS,
}) {
  const [remainingTotalSeconds, setRemainingTotalSeconds] = useState(
    totalSeconds
  );
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;
  const hasFiredTimeoutRef = useRef(false);
  const totalRef = useRef(totalSeconds);
  totalRef.current = totalSeconds;

  const reset = useCallback(() => {
    setRemainingTotalSeconds(totalRef.current);
    hasFiredTimeoutRef.current = false;
  }, []);

  useEffect(() => {
    if (!active) {
      reset();
      return;
    }

    setRemainingTotalSeconds(totalRef.current);
    hasFiredTimeoutRef.current = false;

    const id = setInterval(() => {
      setRemainingTotalSeconds(prev => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [active, reset]);

  useEffect(() => {
    if (!active || remainingTotalSeconds > 0) {
      return;
    }
    if (hasFiredTimeoutRef.current) {
      return;
    }
    hasFiredTimeoutRef.current = true;
    onTimeoutRef.current?.();
  }, [active, remainingTotalSeconds]);

  const t = totalRef.current;
  const lastPhaseSplit = t > 0 ? (t * 30) / 90 : 0; // 30s of the 90s total (scaled)
  const showLongWaitMessage =
    remainingTotalSeconds > 0 && remainingTotalSeconds <= lastPhaseSplit;
  const displayTimerSeconds = getLoaderDisplayTimerSeconds(
    remainingTotalSeconds,
    t
  );
  const progress =
    t > 0
      ? Math.min(((t - remainingTotalSeconds) / t) * 100, 100)
      : 0;

  return {
    /** Remaining of the full 90s (or custom total) — for debugging / rare use */
    remainingTotalSeconds,
    /** Value to show in the “M:SS” part (60→1, then 30→0) */
    displayTimerSeconds,
    showLongWaitMessage,
    progress,
  };
}

/**
 * @param {number} seconds
 * @returns {string} e.g. 90 → "1:30", 59 → "0:59"
 */
export function formatCalculatorLoaderTime(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * @param {(key: string) => string} t
 * @param {boolean} showLongWaitMessage
 * @param {number} displayTimerSeconds
 */
export function getCalculatorLoaderSubtext(
  t,
  showLongWaitMessage,
  displayTimerSeconds
) {
  if (showLongWaitMessage) {
    return `${t('calculator.stepper_info.loader_taking_longer')} ${formatCalculatorLoaderTime(displayTimerSeconds)}`.trim();
  }
  return t('calculator.stepper_info.loader_please_wait').replace(
    '{time}',
    formatCalculatorLoaderTime(displayTimerSeconds)
  );
}
